import {rebaseMarks, storedMarks, type RichText} from './rich-text';
import type {CellElement} from './table-types';
import type {PhotoRequirement} from './types';

/**
 * An element's label with the styles on part of it (DERP-307).
 *
 * Only static text keeps those styles: a header on the page, a heading in a
 * table cell. Every read and write of a label goes through here, so no caller
 * has to know which elements those are, or that the styles must follow the
 * words when the text changes.
 */

type Labelled = PhotoRequirement | CellElement;

function marksOf(el: Labelled) {
  return el.type === 'header' || el.type === 'heading' ? el.config.label_marks : undefined;
}

export function labelText(el: Labelled): RichText {
  return {text: el.label, marks: marksOf(el)};
}

/** `el` with `value` written back as its label. */
export function withLabelText<T extends Labelled>(el: T, value: RichText): T {
  if (el.type !== 'header' && el.type !== 'heading') return {...el, label: value.text};
  // Narrowed to the two types whose config holds marks; the cast puts back the
  // caller's own type, which the spread widens.
  return {
    ...el,
    label: value.text,
    config: {...el.config, label_marks: storedMarks(value.text, value.marks)},
  } as T;
}

/** `el` with its label changed as plain text; the styles follow the words. */
export function relabel<T extends Labelled>(el: T, label: string): T {
  return withLabelText(el, {text: label, marks: rebaseMarks(el.label, marksOf(el), label)});
}
